import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";

import SectionHeader from "./SectionHeader";
import DataTable from "react-data-table-component";
import TableLoader from "./TableLoader";
import FilterSelect from "./FilterSelect";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import Modal from "./Modal";
import TimeEntryForm from "./TimeEntries/TimeEntryForm";
import ConfirmPrompt from "./ConfirmPrompt";

import generateTimeEntryColumns from "./TimeEntries/generateTimeEntryColumns";

import useTimeEntries from "../hooks/domain/timeEntries/useTimeEntries";

import timeEntryService from "../services/timeEntryService";
import labourerService from "../services/labourerService";
import projectService from "../services/projectService";

const typeOptions = [
  { value: 'work', label: 'Work' },
  { value: 'sick', label: 'Sick Leave' }
];

const AdminTimeEntries = () => {
  const [timeEntryPage, setTimeEntryPage] = useState(1);
  const [timeEntryPerPage, setTimeEntryPerPage] = useState(10);

  const [labourers, setLabourers] = useState([]);
  const [projects, setProjects] = useState([]);

  const [selectedType, setSelectedType] = useState(null);
  const [dateRange, setDateRange] = useState(null);

  const [isSubmittingTimeEntryForm, setIsSubmittingTimeEntryForm] = useState(false);

  const [selectedTimeEntry, setselectedTimeEntry] = useState(null);
  const [isTimeEntryFormModalOpen, setIsTimeEntryFormModalOpen] = useState(false);
  const [isConfirmDeleteTimeEntryModalOpen, setIsConfirmDeleteTimeEntryModalOpen] = useState(false);

  const {
    timeEntries,
    count: timeEntryCount,
    isLoading: isLoadingTimeEntries,
    fetchTimeEntries,
  } = useTimeEntries({
    selectedType,
    dateRange,
    page: timeEntryPage,
    perPage: timeEntryPerPage,
    onError: () => toast.error('Something went wrong while loading time entries'),
  });

  const fetchLabourers = async () => {
    try {
      const { labourers } = await labourerService.getLabourers({ page: 1, perPage: 999 });

      setLabourers(labourers);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while fetching labourers');
    }
  };

  const fetchProjects = async () => {
    try {
      const { projects } = await projectService.getProjects({ page: 1, perPage: 999 });

      setProjects(projects);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while fetching projects');
    }
  }

  useEffect(() => {
    fetchTimeEntries();
    fetchLabourers();
    fetchProjects();
  }, [timeEntryPage, timeEntryPerPage, selectedType, dateRange]);

  const handleTimeEntriesPageChange = async (page) => {
    setTimeEntryPage(page);
  }

  const handleTimeEntriesPerPageChange = async (page, newPerPage) => {
    setTimeEntryPerPage(newPerPage);
    setTimeEntryPage(page);
  };

  const handleEditTimeEntryClicked = async (timeEntry) => {
    setselectedTimeEntry(timeEntry);
    setIsTimeEntryFormModalOpen(true);
  };

  const handleSubmitTimeEntryForm = async (payload) => {
    setIsSubmittingTimeEntryForm(true);

    payload.entryType = 'manual';

    try {
      if (selectedTimeEntry) {
        await timeEntryService.updateTimeEntry(selectedTimeEntry._id, payload);
      } else {
        await timeEntryService.createTimeEntry(payload);
      }

      await fetchTimeEntries();
      toast.success(selectedTimeEntry ? 'Time entry updated' : 'Time entry created');
      setselectedTimeEntry(null);
      setIsTimeEntryFormModalOpen(false);
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while submitting the time entry form. Please try again');
    } finally {
      setIsSubmittingTimeEntryForm(false);
    }
  };

  const handleTimeEntryFormClose = () => {
    setIsTimeEntryFormModalOpen(false);
    setselectedTimeEntry(null);
  };

  const handleDeleteTimeEntry = async () => {
    try {
      await timeEntryService.removeTimeEntry(selectedTimeEntry?._id);
      await fetchTimeEntries();

      setselectedTimeEntry(null);
      setIsConfirmDeleteTimeEntryModalOpen(false);
      toast.success('Time entry removed successfuly');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong while deleting the time entry. Please try again');
    }
  };

  const handleRemoveTimeEntryClicked = (timeEntry) => {
    setselectedTimeEntry(timeEntry);
    setIsConfirmDeleteTimeEntryModalOpen(true);
  }

  const labourerSelectOptions = useMemo(() => {
    return labourers.map(labourer => ({
      label: labourer?.user?.fullName,
      value: labourer._id
    }));
  }, [labourers]);

  const projectSelectOptions = useMemo(() => {
    return projects.map(project => ({
      label: project?.name,
      value: project._id
    }));
  }, [projects]);

  return (
    <>
      <Helmet>
        <title>Time Entries | ASE Group</title>
      </Helmet>

      <div className='border border-ase-blue rounded-lg pb-1'>
        <SectionHeader
          title='Time Entries'
          actionsLeft={
            <button
              onClick={() => setIsTimeEntryFormModalOpen(true)}
              className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
            >
              Add
            </button>
          }
          actionsRight={(
            <div className='flex flex-col md:flex-row md:items-center gap-2 w-full'>
              <p className='text-white text-xs'>Type:</p>
              <div className='text-xs  h-[24px]'>
                <FilterSelect
                  options={typeOptions}
                  value={selectedType}
                  onChange={setSelectedType}
                  placeholder='Select type'
                />
              </div>

              <p className='text-white text-xs'>Period:</p>

              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                format='dd/MM/y'
                className="filter-date-range"
              />
            </div>
          )}
        />

        <DataTable
          columns={generateTimeEntryColumns({
            onEditClicked: handleEditTimeEntryClicked,
            onRemoveClicked: handleRemoveTimeEntryClicked
          })}
          data={timeEntries}
          striped
          highlightOnHover
          pagination
          paginationServer
          progressPending={isLoadingTimeEntries}
          progressComponent={<TableLoader />}
          paginationTotalRows={timeEntryCount}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          paginationPerPage={timeEntryPerPage}
          onChangePage={handleTimeEntriesPageChange}
          onChangeRowsPerPage={handleTimeEntriesPerPageChange}
        />
      </div>

      <Modal isOpen={isTimeEntryFormModalOpen}>
        <TimeEntryForm
          title={selectedTimeEntry ? 'Edit Time Entry' : 'Add Time Entry'}
          shouldShowLabourerField={true}
          shouldShowProjectField={true}
          projectSelectOptions={projectSelectOptions}
          labourerSelectOptions={labourerSelectOptions}
          timeEntry={selectedTimeEntry}
          isLoading={isSubmittingTimeEntryForm}
          onSubmit={handleSubmitTimeEntryForm}
          onCloseClicked={handleTimeEntryFormClose}
        />
      </Modal>

      <Modal isOpen={isConfirmDeleteTimeEntryModalOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete this time entry?`}
          onConfirm={handleDeleteTimeEntry}
          onCancel={() => setIsConfirmDeleteTimeEntryModalOpen(false)}
        />
      </Modal>
    </>
  )
};

export default AdminTimeEntries;
